import React, { useEffect, useMemo, useRef } from 'react';
import '../../styling/Modal.css';

function NotificationModal({
  show,
  message,
  onClose,
  autoCloseMs = 1500,
  disableAutoClose, // optional override
}) {
  // treat any "creating..." text as a non-autoclose state
  const isCreating =
    typeof message === 'string' &&
    message.trim().toLowerCase().startsWith('creating');

  const shouldAutoClose = useMemo(() => {
    if (!show) return false;
    if (disableAutoClose != null) return !disableAutoClose;
    return !isCreating;
  }, [show, disableAutoClose, isCreating]);

  // keep latest onClose without retriggering effects
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);

  // run timer only on false -> true transition
  const prevShowRef = useRef(false);
  const timerRef = useRef(null);

  useEffect(() => {
    // clear any old timer on unmount/show=false
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const wasShowing = prevShowRef.current;
    prevShowRef.current = show;

    // if not showing or not auto-closing, ensure no timer
    if (!show || !shouldAutoClose) {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      return;
    }

    // only start a timer when we *just opened* (false -> true)
    if (!wasShowing && show) {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        if (typeof onCloseRef.current === 'function') onCloseRef.current();
        timerRef.current = null;
      }, autoCloseMs);
    }
    // NOTE: do not restart timer on re-renders while show stays true
  }, [show, shouldAutoClose, autoCloseMs]);

  const handleManualClose = (e) => {
    e.preventDefault();
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (typeof onCloseRef.current === 'function') onCloseRef.current();
  };

  return (
    <div
      className={`modal fade ${show ? 'show modal-backdrop-transparent' : ''}`}
      tabIndex="-1"
      aria-labelledby="notificationModalLabel"
      aria-hidden={!show}
      role="dialog"
      style={{ display: show ? 'block' : 'none' }}
    >
      <div className="modal-dialog">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title" id="notificationModalLabel">Notification</h5>
            {/* Hide the close button when "creating" or when there's no handler */}
            {!isCreating && typeof onClose === 'function' && (
              <button
                type="button"
                className="btn-close"
                onClick={handleManualClose}
                aria-label="Close"
              />
            )}
          </div>
          <div className="modal-body">{message}</div>
        </div>
      </div>
    </div>
  );
}

export default NotificationModal;
